import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Label } from '@progress/kendo-react-labels';

import { defaultColors } from '../assets/theme';
import { ColorPicker } from '../../../components/color-picker';
import { SingleUpload } from '../../../components/single-upload';
import { Theme as ThemeModel, ThemeColor } from '../../../types/theme';
import { FontCombobox } from '../../../components/combobox/font-combobox';
import { IconScaleCombobox } from '../../../components/combobox/icon-scale-combobox';

const Theme: React.FC<{
  theme: ThemeModel | undefined;
  hideResourcesTab?: boolean;
  setTheme: (theme: ThemeModel) => void;
}> = ({ theme, hideResourcesTab = false, setTheme }) => {
  const defaultValue = () => {
    let newTheme: ThemeModel = { ...theme };
    newTheme.fontFamily = '';
    newTheme.logoUrl = '';
    newTheme.footerUrl = '';
    newTheme.colors = defaultColors;
    newTheme.iconScale = 'sm';

    setTheme(newTheme);
  };

  const onChangeThemeColor = (index: number, color: string) => {
    let newTheme: ThemeModel = { ...theme };
    newTheme.colors![index].color = color;
    setTheme(newTheme);
  };

  React.useEffect(() => {
    if (!theme) defaultValue();
  }, [theme]);

  return (
    <>
      <Row>
        <Col className={'d-flex flex-column'}>
          <Label>Typeface</Label>
          <span>
            Please type a font to be used to display text within the game
          </span>
        </Col>
      </Row>
      <Row className={'mt-3'}>
        <Col
          className={'d-flex flex-column gap-1'}
          xs={'12'}
          sm={'12'}
          md={'12'}
          lg={'3'}
          xl={'3'}>
          <Label>Font</Label>
          <FontCombobox
            value={theme?.fontFamily ? theme?.fontFamily : null}
            onChange={(e) => {
              setTheme({
                ...theme,
                fontFamily: e.target.value?.toString() ?? ''
              });
            }}
          />
        </Col>
      </Row>
      <Row className={'mt-3'}>
        <Col>
          <Label>Colours</Label>
        </Col>
      </Row>
      <Row className={'mt-3'}>
        <Col className={'d-flex flex-wrap'}>
          {theme?.colors?.map((color: ThemeColor, index: number) => (
            <div className={'mr-3 mb-3'} key={index}>
              <ColorPicker
                key={index}
                view={'gradient'}
                description={color?.type}
                value={color?.color}
                onChange={(val) => {
                  onChangeThemeColor(index, val!);
                }}
              />
            </div>
          ))}
        </Col>
      </Row>
      <Row className={'mt-3 mb-3'}>
        <Col
          className={'d-flex flex-column gap-1'}
          xs={'12'}
          sm={'12'}
          md={'12'}
          lg={'3'}
          xl={'3'}>
          <Label>Icon Size</Label>
          <span>Customise icon scaling on map</span>
          <IconScaleCombobox
            value={theme?.iconScale ?? 'sm'}
            onChange={(e) => {
              setTheme({
                ...theme,
                iconScale: e.target.value.value
              });
            }}
          />
        </Col>
      </Row>
      <Row className={'mt-3 mb-3'}>
        <Col className={'d-flex flex-column'}>
          <Label>Logo</Label>
          <span>Customise the logo displayed in the opening screens</span>
        </Col>
      </Row>
      <SingleUpload
        uploadTitle={'Upload logo'}
        fileType={['image']}
        url={theme?.logoUrl}
        hideResourcesTab={hideResourcesTab}
        width={200}
        onUpload={(e) => {
          setTheme({
            ...theme,
            logoUrl: e.blobUrl
          });
        }}
        source="Logo"
      />
      <Row className={'mt-3 mb-3'}>
        <Col className={'d-flex flex-column'}>
          <Label>Footer</Label>
          <span>Add a transparent .png as a footer</span>
        </Col>
      </Row>
      <SingleUpload
        uploadTitle={'Upload footer'}
        fileType={['image']}
        hideResourcesTab={hideResourcesTab}
        url={theme?.footerUrl}
        onUpload={(e) => {
          setTheme({
            ...theme,
            footerUrl: e.blobUrl
          });
        }}
        source="Footer"
      />
    </>
  );
};

export default Theme;
